import { jsx, jsxs } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import logger from '@agoras/logger';
import { mergeStyleSets, FontWeights, NeutralColors, Stack, Text, Shimmer, ShimmerElementType, PrimaryButton, ScreenWidthMaxLarge } from '@fluentui/react';
import { appsourceRouteMatch } from '../../../router/appsource.esm.js';
import { homeContext } from '../context.esm.js';
import Searchbar from '../../Searchbar/Searchbar.esm.js';
import { UNIFIED_SEARCHBAR_DROPDOWN, SEARCHBAR_DEFAULTS } from '../../Searchbar/constants.esm.js';
import { useHydration, useBreakpoint } from '@agoras/hooks';
import { useRef, useEffect } from 'react';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var classNames = mergeStyleSets({
    searchStackItem: _define_property({
        width: '772px'
    }, "@media screen and (max-width: ".concat(ScreenWidthMaxLarge, "px)"), {
        width: '100%'
    }),
    title: {
        color: NeutralColors.white,
        fontWeight: FontWeights.semibold,
        letterSpacing: '1px'
    }
});
var Hero = function(param) {
    var navigate = param.navigate, locale = param.locale, bannerBackgroundImage = param.bannerBackgroundImage, unifiedExperience = param.unifiedExperience, searchProps = param.searchProps;
    var t = useTranslation('home').t;
    var isHydrated = useHydration();
    var searchbarRef = useRef(null);
    var _useBreakpoint = _sliced_to_array(useBreakpoint('lg'), 1), isLargeOrSmaller = _useBreakpoint[0];
    useEffect(function() {
        var headerHeight = isLargeOrSmaller ? 104 : 92;
        var rootMargin = "-".concat(headerHeight, "px 0px 0px 0px");
        var observer = new IntersectionObserver(function(param) {
            var _param = _sliced_to_array(param, 1), entry = _param[0];
            searchProps.setSearchInHero(entry.isIntersecting);
        }, {
            threshold: 0.1,
            rootMargin: rootMargin
        });
        var currentRef = searchbarRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }
        return function() {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [
        isLargeOrSmaller
    ]);
    var handleSeeAllProductsClick = function() {
        var navigateOptions = {
            type: 'App'
        };
        var appsourceRoute = appsourceRouteMatch(_object_spread_props(_object_spread({}, navigateOptions), {
            params: {
                locale: locale
            }
        }));
        logger.info(JSON.stringify({
            href: "".concat(appsourceRoute.pathname).concat(appsourceRoute.search)
        }), _object_spread({
            action: 'Click',
            actionModifier: 'Internal Link'
        }, homeContext));
        navigate(navigateOptions);
    };
    var _searchProps_isSearchInHero;
    return /*#__PURE__*/ jsx(Stack, {
        horizontalAlign: "center",
        verticalAlign: "center",
        styles: {
            root: {
                width: '100%',
                backgroundImage: "url(".concat(bannerBackgroundImage, ")"),
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                padding: '56px 28px'
            }
        },
        children: /*#__PURE__*/ jsxs(Stack, {
            tokens: {
                childrenGap: '32px'
            },
            styles: {
                root: {
                    width: '100%'
                }
            },
            horizontalAlign: "center",
            children: [
                /*#__PURE__*/ jsxs(Stack, {
                    tokens: {
                        childrenGap: '16px'
                    },
                    styles: {
                        root: {
                            width: '100%'
                        }
                    },
                    horizontalAlign: "center",
                    children: [
                        /*#__PURE__*/ jsxs(Text, {
                            variant: "small",
                            className: classNames.title,
                            children: [
                                "MICROSOFT ",
                                unifiedExperience ? 'MARKETPLACE' : 'APPSOURCE'
                            ]
                        }),
                        /*#__PURE__*/ jsx(Text, {
                            variant: "xxLarge",
                            as: "h1",
                            styles: {
                                root: {
                                    color: NeutralColors.white
                                }
                            },
                            children: t('Get the right product for your business needs')
                        })
                    ]
                }),
                /*#__PURE__*/ jsxs("div", {
                    ref: searchbarRef,
                    className: classNames.searchStackItem,
                    children: [
                        /*#__PURE__*/ jsx(Shimmer, {
                            isDataLoaded: isHydrated,
                            shimmerElements: [
                                {
                                    type: ShimmerElementType.line,
                                    width: '100%',
                                    height: 32
                                }
                            ]
                        }),
                        isHydrated && /*#__PURE__*/ jsx(Searchbar, {
                            mfeContext: homeContext,
                            filters: searchProps === null || searchProps === void 0 ? void 0 : searchProps.filters,
                            onSearch: searchProps === null || searchProps === void 0 ? void 0 : searchProps.onSearch,
                            showCallout: (_searchProps_isSearchInHero = searchProps === null || searchProps === void 0 ? void 0 : searchProps.isSearchInHero) !== null && _searchProps_isSearchInHero !== void 0 ? _searchProps_isSearchInHero : true,
                            dropdownOptions: unifiedExperience ? UNIFIED_SEARCHBAR_DROPDOWN : SEARCHBAR_DEFAULTS.DROPDOWN
                        })
                    ]
                }),
                /*#__PURE__*/ jsx(PrimaryButton, {
                    styles: {
                        root: {
                            width: 'fit-content'
                        }
                    },
                    onClick: handleSeeAllProductsClick,
                    "data-bi-name": "actionBanner See all apps button",
                    "data-bi-area": "homePage",
                    children: t('See all products')
                })
            ]
        })
    });
};

export { Hero as default };
